import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Box,
  useToast 
} from '@chakra-ui/react';
import Form from '@rjsf/chakra-ui';
import validator from "@rjsf/validator-ajv8";
import { reverseTransformData, transformData } from '../../../util/helper';
import { getPersonalityAliasSuggestions, getPersonalityPersonaliaData, updatePersonalityPersonaliaData } from '../../../services/Personality';
import TypeaheadWidget from '../../../CommonComponents/TypeaheadWidget';
import { RjsfSubmitButton } from '../../../RJSFCommonSchemas';
import FormLoader from '../../../CommonComponents/FormLoader';
import { showErrorToast, showInfoToast, showSuccessToast } from "../../../util/errorHandler";


const Personalia = () => {
  const { alias } = useParams();
  const [processedData, setProcessedData] = useState({});
  const [originalData, setOriginalData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [quickSave, setQuickSave] = useState(false);
  const toast = useToast();

  const schema =  {
    "title": "Personality Perconalia Fields",
  "type": "object",
  "properties": {
    "date_of_birth": {
      "type": "string",
      "title": "Date of Birth"
    },
    "time_of_birth": {
      "type": "string",
      "title": "Time of Birth"
    },
    "date_of_death": {
      "type": "string",
      "title": "Date of Death"
    },
    "zodiac_sign": {
      "type": "string",
      "title": "Zodiac Sign"
    },
    "place_of_birth": {
      "type": "string",
      "title": "Place of Birth"
    },
    "state_country_of_birth": {
      "type": "string",
      "title": "State or Country of Birth"
    },
    "state_country_of_death": {
      "type": "string",
      "title": "State or Country of Death",
      "default": ""
    },
    "official_website": {
      "type": "string",
      "title": "Official Website"
    },
    "place_of_death": {
      "type": "string",
      "title": "Place of Death"
    },
    "age": {
      "type": "string",
      "title": "Age (Calculated)",
      "readOnly": true
    },
    "nationality": {
      "type": "string",
      "title": "Nationality"
    },
    "marriageregistrationdate": {
      "type": "string",
      "title": "Marriage Registration Date"
    },
    "year_of_wedding": {
      "type": "string",
      "title": "Year of Wedding"
    },
    "spouse": {
      "type": "array",
      "items": {
        "type": "object",
        "properties": {
          "alias": { "type": "string" },
        }
      }
    },
    "children": {
      "type": "array",
      "items": {
        "type": "object",
        "properties": {
          "alias": { "type": "string" },
        }
      }
    },
    "parents": {
      "type": "array",
      "items": {
        "type": "object",
        "properties": {
          "alias": { "type": "string" },
        }
      }
    },
    "grandparents": {
      "type": "array",
      "items": {
        "type": "object",
        "properties": {
          "alias": { "type": "string" },
        }
      }
    },
    "ancestors": {
      "type": "array",
      "items": {
        "type": "object",
        "properties": {
          "alias": { "type": "string" },
        }
      }
    },
    "siblings": {
      "type": "array",
      "items": {
        "type": "object",
        "properties": {
          "alias": { "type": "string" },
        }
      }
    },
    "is_publisher": {
      "type": "string",
      "title": "Is Publisher"
    },
    "national_art_treasure_non_exportable": {
      "type": "string",
      "title": "National Art Treasure (Non-Exportable)",
      "enum": ["", "Yes", "No"],
      "enumNames": ["Select", "Yes", "No"]
    },
    "isChronicledHighlight": {
      "type": "string",
      "title": "Is Chronicled Highlight"
    },
    "gender": {
      "type": "string",
      "title": "Gender",
      "enum": ["", "Male", "Female", "Transgender", "Non-binary", "Other", "Prefer not to say"],
      "enumNames": ["Select Gender", "Male", "Female", "Transgender", "Non-binary", "Other", "Prefer not to say"]
    },
    "country": {
      "type": "string",
      "title": "Country"
    },
    "official_social_media_page": {
      "type": "string",
      "title": "Official Social Media Page"
    },
    "signatures": {
      "type": "string",
      "title": "Signatures"
    },
    "handwriting": {
      "type": "string",
      "title": "Handwriting"
    },
    "other_important_websites": {
      "type": "string",
      "title": "Other Important Websites"
    },
    "other_important_social_media_pages": {
      "type": "string",
      "title": "Other Important Social Media Pages"
    }
  }
  }
  
  const uiSchema = {
    ...RjsfSubmitButton(isSubmitting),
    "date_of_birth": { "ui:emptyValue": "" },
    "time_of_birth": { "ui:emptyValue": "" },
    "date_of_death": { "ui:emptyValue": "" },
    "zodiac_sign": { "ui:emptyValue": "" },
    "place_of_birth": { "ui:emptyValue": "" },
    "state_country_of_birth": { "ui:emptyValue": "" },
    "state_country_of_death": { "ui:emptyValue": "" },
    "official_website": { "ui:emptyValue": "" },
    "place_of_death": { "ui:emptyValue": "" },
    "age": { 
      "ui:emptyValue": "",
      "ui:readonly": true,
      "ui:help": "Age is automatically calculated from Date of Birth and Date of Death"
    },
    "nationality": { "ui:emptyValue": "" },
    "marriageregistrationdate": { "ui:emptyValue": "" },
    "year_of_wedding": { "ui:emptyValue": "" },
    "spouse": {
      items: {
        alias: {
          "ui:widget": "personalityAliasTypeahead", // Use the custom TypeaheadWidget
        },
      },
    },
    "parents": {
      items: {
        alias: {
          "ui:widget": "personalityAliasTypeahead", // Use the custom TypeaheadWidget
        },
      },
    },
    "grandparents": {
      items: {
        alias: {
          "ui:widget": "personalityAliasTypeahead", // Use the custom TypeaheadWidget
        },
      },
    },
    "ancestors": {
      items: {
        alias: {
          "ui:widget": "personalityAliasTypeahead", // Use the custom TypeaheadWidget
        },
      },
    },
    "siblings": {
      items: {
        alias: {
          "ui:widget": "personalityAliasTypeahead", // Use the custom TypeaheadWidget
        },
      },
    },

    "children": {
      items: {
        alias: {
          "ui:widget": "personalityAliasTypeahead", // Use the custom TypeaheadWidget
        },
      },
    },
    "is_publisher": {
      "ui:emptyValue": ""
    },
    "national_art_treasure_non_exportable": {
      "ui:emptyValue": ""
    },
    "isChronicledHighlight": {
      "ui:emptyValue": ""
    },
    "gender": {
      "ui:emptyValue": ""
    },
    "country": {
      "ui:emptyValue": ""
    },
    "official_social_media_page": {
      "ui:emptyValue": ""
    },
    "signatures": {
      "ui:emptyValue": ""
    },
    "handwriting": {
      "ui:emptyValue": ""
    },
    "other_important_websites": {
      "ui:emptyValue": ""
    },
    "other_important_social_media_pages": {
      "ui:emptyValue": ""
    },
    };

  // Calculate age from date of birth and date of death
  const calculateAge = (dateOfBirth, dateOfDeath) => {
    if (!dateOfBirth) return '';
    
    try {
      // Parse the date of birth (handle different formats)
      let birthDate;
      if (dateOfBirth.includes(' ')) {
        // Handle format like "15 Feb 1924"
        birthDate = new Date(dateOfBirth);
      } else {
        // Handle format like "1924-02-15"
        birthDate = new Date(dateOfBirth);
      }
      
      if (isNaN(birthDate.getTime())) return '';
      
      const currentDate = dateOfDeath ? new Date(dateOfDeath) : new Date();
      const age = currentDate.getFullYear() - birthDate.getFullYear();
      const monthDiff = currentDate.getMonth() - birthDate.getMonth();
      
      // Adjust age if birthday hasn't occurred this year
      if (monthDiff < 0 || (monthDiff === 0 && currentDate.getDate() < birthDate.getDate())) {
        return (age - 1).toString();
      }
      
      return age.toString();
    } catch (error) {
      console.error('Error calculating age:', error);
      return '';
    }
  };

  // State to track form data with calculated age
  const [formData, setFormData] = useState({});

  // Function to identify changed items for array fields
  const getChangedItems = (currentData, originalData, fieldName) => {
    if (!currentData[fieldName] || !originalData[fieldName]) return [];
    
    const changedItems = [];
    const currentArray = currentData[fieldName];
    const originalArray = originalData[fieldName];

    // Check for modified items by comparing each field individually
    currentArray.forEach((currentItem, index) => {
      const originalItem = originalArray[index];
      
      if (originalItem) {
        // Compare each field individually
        const hasChanges = Object.keys(currentItem).some(key => {
          return currentItem[key] !== originalItem[key];
        });
        
        if (hasChanges) {
          // This is an updated item
          changedItems.push({
            ...currentItem,
            type: 'update'
          });
        }
      } else {
        // This is a new item
        changedItems.push({
          ...currentItem,
          type: 'add'
        });
      }
    });
    
    // Check for deleted items (items that exist in original but not in current)
    originalArray.forEach((originalItem, index) => {
      if (!currentArray[index]) {
        changedItems.push({
          ...originalItem,
          type: 'delete'
        });
      }
    });
    
    return changedItems;
  };

  // Update form data when processedData changes
  useEffect(() => {
    if (processedData && Object.keys(processedData).length > 0) {
      const calculatedAge = calculateAge(processedData.date_of_birth, processedData.date_of_death);
      setFormData({
        ...processedData,
        age: calculatedAge
      });
    }
  }, [processedData]);

  // Handle form changes and recalculate age
  const handleFormChange = (formData) => {
    const calculatedAge = calculateAge(formData.formData?.date_of_birth, formData.formData?.date_of_death);
    setFormData({
      ...formData.formData,
      age: calculatedAge
    });
  };


  const fetchPersonalityAliasSuggestions = async (inputValue) => {

    const fetchedMasterListAliasSuggestions = await loadPersonalityAliasSuggestions(inputValue);

    // Ensure inputValue is string and trimmed
    if (!inputValue || typeof inputValue !== "string" || !fetchedMasterListAliasSuggestions) {
      return [];
    }

    return fetchedMasterListAliasSuggestions?.map((item) => ({
      label: item.alias,
      value: item.alias
    }));
  };

  const loadPersonalityAliasSuggestions = async (inputValue) => {
    try {
      const result = await getPersonalityAliasSuggestions(20, 1, inputValue);
      return result?.data?.data || [];
    } catch (error) {
            showErrorToast(toast, "Failed to load document types");
    }
  };

  const widgets = {
    personalityAliasTypeahead: (props) => (
      <TypeaheadWidget
        {...props}
        fetchOptions={fetchPersonalityAliasSuggestions}
      />
    ),

  };


  // Fetch initial data
  useEffect(() => {
    const getPrefaceData = async () => {
      try {
        if (alias) {
          const result = await getPersonalityPersonaliaData(alias);
          if (result) {

            const fieldsToTransform = [
              'poster_image_carousel_alias', 
              'other_publicity_image_carousel_alias'
            ];
            const transformedData = transformData(result, fieldsToTransform);
            setProcessedData(transformedData);
            setOriginalData(transformedData); // Store original data for comparison
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
                showErrorToast(toast, "Failed to fetch data");
      } finally {
        setIsLoading(false);
      }
    };

    getPrefaceData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alias, toast, quickSave]);

  // Submit updated data
  const handleSubmit = async (formData) => {
    try {
      if (alias) {
        setIsSubmitting(true);
        
        // Get changed items for each array field
        const arrayFields = ['spouse', 'children', 'parents', 'grandparents', 'ancestors', 'siblings'];
        const changedData = {};
        let hasChanges = false;
        
        // Check for changes in array fields
        arrayFields.forEach(fieldName => {
          const changedItems = getChangedItems(formData, originalData, fieldName);
          if (changedItems.length > 0) {
            changedData[fieldName] = changedItems;
            hasChanges = true;
          }
        });
        
        // Check for changes in non-array fields
        const nonArrayFields = [
          'date_of_birth', 'time_of_birth', 'date_of_death', 'zodiac_sign',
          'place_of_birth', 'state_country_of_birth', 'state_country_of_death',
          'official_website', 'place_of_death', 'age', 'nationality',
          'marriageregistrationdate', 'year_of_wedding',
          'is_publisher', 'national_art_treasure_non_exportable', 'isChronicledHighlight',
          'gender', 'country', 'official_social_media_page',
          'signatures', 'handwriting', 'other_important_websites', 'other_important_social_media_pages'
        ];

        nonArrayFields.forEach(fieldName => {
          if (formData[fieldName] !== originalData[fieldName]) {
            changedData[fieldName] = formData[fieldName];
            hasChanges = true;
          }
        });
        
        if (hasChanges) {
          const fieldsToTransform = [
            'poster_image_carousel_alias', 
            'other_publicity_image_carousel_alias'
          ];
          const updatedFormData = reverseTransformData(changedData, fieldsToTransform);
          
          const result = await updatePersonalityPersonaliaData(alias, updatedFormData);

          if (result?.status === 200) {
            setQuickSave(prev => !prev);
            setProcessedData(formData); // Update processed data to reflect current form state
                        showSuccessToast(toast, "Personalia data updated successfully");
          }
        } else {
          // No changes detected
                    showInfoToast(toast, "No changes detected");
        }
      }
    } catch (error) {
      console.error('Error updating film data:', error);
            showErrorToast(toast, "Failed to update data");
    }
    finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box maxW="1200px">
      {isLoading ? (
        <FormLoader />
      ) : (
        <Form 
          schema={schema} 
          uiSchema={uiSchema} 
          formData={formData} 
          validator={validator} 
          onSubmit={(e) => handleSubmit(e.formData)} 
          onChange={handleFormChange}
          widgets={widgets} 
        />
      )}
    </Box>
  );
};

export default Personalia;